/*
  Load navigation images on hover.

  Mega-menu banners, dropdown banners and menu item icons are only ever seen after
  the shopper hovers, focuses or taps a menu item, so nothing in the navigation
  should cost bandwidth on first paint. The Liquid emits them with `data-src` /
  `data-srcset` (lazysizes markup) and this file promotes them the instant the menu
  item the shopper is pointing at starts to open - well before lazysizes' own
  throttled pass would get to them.

  The mobile drawer is handled separately: theme.js *moves* the desktop nav into
  `#navigation-mobile` when the hamburger is tapped, so the whole drawer is
  hydrated on open rather than per item.
*/
(function () {
  'use strict';

  // Where a hover is worth reacting to at all. Keeps the delegated listener from
  // doing any work for pointer moves outside the navigation.
  var NAV_ROOTS = [
    '.header__inline-menu',
    '[data-navigation]',
    '[data-navigation-mobile]',
    '.site-nav-mobile',
    '.halo-sidebar_menu',
    '.header__menu-vertical',
    '.cmd__panel'
  ].join(',');

  // The unit we hydrate as one piece - a whole top-level menu item and its dropdown.
  var SCOPES = '.menu-lv-item, .cmd__root, [data-menu-lazy-scope]';

  // `:not([data-menu-hydrated])` matters: lazysizes leaves `data-srcset` in place
  // after it unveils an image, so the marker is what tells us an image is done.
  var IMAGES = [
    'img[data-src]',
    'img[data-srcset]',
    'source[data-src]',
    'source[data-srcset]'
  ]
    .map(function (selector) {
      return selector + ':not([data-menu-hydrated])';
    })
    .join(',');

  var POINTER_EVENTS = ['pointerover', 'focusin', 'touchstart'];

  function load(img) {
    img.setAttribute('data-menu-hydrated', '');

    if (window.lazySizes && window.lazySizes.loader && img.classList.contains('lazyload')) {
      try {
        // Hand it to lazysizes so the .lazyloading / .lazyloaded classes the theme
        // CSS keys off still get applied.
        window.lazySizes.loader.unveil(img);
        return;
      } catch (e) {
        /* fall through to promoting the attributes by hand */
      }
    }

    var sizes = img.getAttribute('data-sizes');
    var srcset = img.getAttribute('data-srcset');
    var src = img.getAttribute('data-src');

    if (sizes && sizes !== 'auto') {
      img.setAttribute('sizes', sizes);
      img.removeAttribute('data-sizes');
    }
    if (srcset) {
      img.setAttribute('srcset', srcset);
      img.removeAttribute('data-srcset');
    }
    if (src) {
      img.setAttribute('src', src);
      img.removeAttribute('data-src');
    }
  }

  function hydrate(root) {
    if (!root) return;

    var images = root.querySelectorAll(IMAGES);
    if (!images.length) return;

    Array.prototype.forEach.call(images, load);
    stopWhenDone();
  }

  // Once every deferred navigation image has been promoted there is nothing left
  // for the delegated pointer handler to do, so drop it.
  function stopWhenDone() {
    if (document.querySelector(IMAGES)) return;

    POINTER_EVENTS.forEach(function (type) {
      document.removeEventListener(type, onPointer, { capture: true });
    });
  }

  // Hydrate the *outermost* menu item under the pointer, not the innermost, so
  // hovering a third-level link still pulls in the banners of the mega menu it
  // belongs to.
  function outermostScope(node) {
    var scope = null;
    var el = node;

    while (el && el !== document.body) {
      if (el.matches && el.matches(SCOPES)) scope = el;
      el = el.parentElement;
    }

    return scope;
  }

  function onPointer(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest(NAV_ROOTS)) return;

    hydrate(outermostScope(target));
  }

  POINTER_EVENTS.forEach(function (type) {
    document.addEventListener(type, onPointer, { passive: true, capture: true });
  });

  // Opening the mobile drawer: hydrate everything in it, since theme.js relocates
  // the desktop menu markup into the drawer at that moment.
  function hydrateMobileNav() {
    ['#navigation-mobile', '.halo-sidebar_menu', '[data-navigation-mobile]'].forEach(function (sel) {
      Array.prototype.forEach.call(document.querySelectorAll(sel), hydrate);
    });
  }

  document.addEventListener(
    'pointerdown',
    function (event) {
      if (!event.target.closest) return;
      if (event.target.closest('.mobileMenu-toggle, [data-cmd-open]')) hydrateMobileNav();
    },
    { passive: true, capture: true }
  );

  // Backstop for any other code path that opens the drawer, and for the markup
  // theme.js moves in after the toggle was tapped.
  if ('MutationObserver' in window) {
    new MutationObserver(function () {
      if (document.body.classList.contains('menu_open')) hydrateMobileNav();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  window.menuImageLazy = { hydrate: hydrate, hydrateMobileNav: hydrateMobileNav };
})();
